import type { SupabaseClient as SupabaseJsClient } from '@supabase/supabase-js';
import type { SupabaseClient } from '../../lib/supabase';

export interface HuddleRow {
  id: string;
  project_id: string;
  started_by: string;
  started_at: string;
  ended_at: string | null;
  state: 'live' | 'ended' | 'processing' | 'done' | 'failed';
  playback_state: 'pending' | 'ready' | 'empty' | 'failed';
  transcript_state: 'pending' | 'done' | 'empty' | 'failed' | 'too_long';
  max_participants: number;
  room_comment_id: string | null;
  failed_reason: 'too_long' | 'error' | null;
  transcribed_at: string | null;
  playback_path: string | null;
  playback_bytes: number | null;
  playback_duration_ms: number | null;
  playback_deleted_at: string | null;
}

export interface HuddleTranscriptRow {
  id: string;
  huddle_id: string;
  chunk_id: string | null;
  speaker_user_id: string;
  start_ms: number;
  end_ms: number;
  text: string;
  lang: string | null;
}

export interface HuddleParticipant {
  id: string;
  huddle_id: string;
  user_id: string;
  name: string;
  joined_at: string;
  left_at: string | null;
}

export interface HuddleTranscriptLine extends HuddleTranscriptRow {
  speaker_name: string;
}

/**
 * The huddles grant is deliberately a column-list grant. Keep this list in
 * step with that grant: PostgREST refuses select('*') because it would also
 * ask for worker-only processing columns.
 */
export const HUDDLE_COLUMNS =
  'id, project_id, started_by, started_at, ended_at, state, playback_state, transcript_state, max_participants, room_comment_id, failed_reason, transcribed_at, playback_path, playback_bytes, playback_duration_ms, playback_deleted_at';

// The committed generated Database type predates the huddles migrations. The
// cast is local to this pre-migration reader and can go away when types are
// regenerated by the migration owner.
function huddleClient(client: SupabaseClient): SupabaseJsClient {
  return client;
}

type ProfileName = { display_name: string; email: string };
type ParticipantRead = Omit<HuddleParticipant, 'name'> & {
  profiles: ProfileName | ProfileName[] | null;
};
type TranscriptRead = HuddleTranscriptRow & { profiles: ProfileName | ProfileName[] | null };
const PAGE_SIZE = 500;
async function pages<T>(
  query: () => {
    range: (
      from: number,
      to: number,
    ) => PromiseLike<{ data: unknown[] | null; error: { message: string } | null }>;
  },
): Promise<T[]> {
  const rows: T[] = [];
  for (let offset = 0; ; offset += PAGE_SIZE) {
    const { data, error } = await query().range(offset, offset + PAGE_SIZE - 1);
    if (error) throw new Error(error.message);
    const page = (data ?? []) as T[];
    rows.push(...page);
    if (page.length < PAGE_SIZE) return rows;
  }
}

function profileName(profile: ProfileName | ProfileName[] | null): string {
  if (Array.isArray(profile)) {
    return profileName(profile[0] ?? null);
  }
  if (profile === null) {
    return 'Someone';
  }
  return profile.display_name.trim() === '' ? profile.email : profile.display_name;
}

export async function listRoomHuddles(
  client: SupabaseClient,
  projectId: string,
): Promise<HuddleRow[]> {
  return pages<HuddleRow>(() =>
    huddleClient(client)
      .from('huddles')
      .select(HUDDLE_COLUMNS)
      .eq('project_id', projectId)
      .order('started_at', { ascending: false })
      .order('id', { ascending: true }),
  );
}

/** An unavailable pre-migration read has no cards, so MessagesPage keeps its normal bodies. */
export function huddlesByRoomCommentId(rows: HuddleRow[] | undefined): Map<string, HuddleRow> {
  return new Map(
    (rows ?? []).flatMap((huddle) =>
      huddle.room_comment_id === null ? [] : [[huddle.room_comment_id, huddle] as const],
    ),
  );
}

export async function listHuddleParticipants(
  client: SupabaseClient,
  huddleId: string,
): Promise<HuddleParticipant[]> {
  const data = await pages<ParticipantRead>(() =>
    huddleClient(client)
      .from('huddle_participants')
      .select('id, huddle_id, user_id, joined_at, left_at, profiles:user_id (display_name, email)')
      .eq('huddle_id', huddleId)
      .order('joined_at')
      .order('id', { ascending: true }),
  );
  return (
    (data ?? []) as unknown as Array<{
      id: string;
      huddle_id: string;
      user_id: string;
      joined_at: string;
      left_at: string | null;
      profiles: ProfileName | ProfileName[] | null;
    }>
  ).map((row) => ({
    id: row.id,
    huddle_id: row.huddle_id,
    user_id: row.user_id,
    name: profileName(row.profiles),
    joined_at: row.joined_at,
    left_at: row.left_at,
  }));
}

export async function listHuddleTranscript(
  client: SupabaseClient,
  huddleId: string,
): Promise<HuddleTranscriptLine[]> {
  const data = await pages<TranscriptRead>(() =>
    huddleClient(client)
      .from('huddle_transcripts')
      .select(
        'id, huddle_id, chunk_id, speaker_user_id, start_ms, end_ms, text, lang, profiles:speaker_user_id (display_name, email)',
      )
      .eq('huddle_id', huddleId)
      .order('start_ms')
      .order('id', { ascending: true }),
  );
  return (
    (data ?? []) as unknown as Array<
      HuddleTranscriptRow & { profiles: ProfileName | ProfileName[] | null }
    >
  ).map(({ profiles, ...row }) => ({ ...row, speaker_name: profileName(profiles) }));
}

export function playbackUrlExpires(playbackDurationMs: number): number {
  return Math.min(Math.round(playbackDurationMs / 1000) + 1800, 7200);
}

export async function signPlayback(
  client: SupabaseClient,
  playbackPath: string,
  playbackDurationMs: number,
): Promise<string> {
  const { data, error } = await huddleClient(client)
    .storage.from('huddle-playback')
    .createSignedUrl(playbackPath, playbackUrlExpires(playbackDurationMs));
  if (error || data?.signedUrl === undefined) {
    throw new Error(error?.message ?? 'Playback could not be signed.');
  }
  return data.signedUrl;
}

export async function retryHuddleProcessing(
  client: SupabaseClient,
  huddleId: string,
): Promise<void> {
  const { error } = await huddleClient(client).rpc('retry_huddle_processing', {
    p_huddle: huddleId,
  });
  if (error) {
    throw new Error(error.message);
  }
}
